import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import type { YearCashflow } from '../utils/financialCalculations';

const C = { bdr: '#D9E2EC', t3: '#8899AA', grn: '#10B981', rd: '#EF4444', sec: '#00A3C4', pri: '#0B1F4A' };

function CfTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#D9E2EC] rounded-lg px-3 py-2 shadow-lg text-[11px] max-w-[260px]">
      <div className="font-bold text-[#0B1F4A] mb-1">Jaar {label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-1.5 mb-0.5">
          <div className="w-2 h-2 rounded-sm shrink-0" style={{ background: p.color }} />
          <span className="text-[#3D4F6F]">{p.name}: <strong>€{(p.value / 1e6).toFixed(2)}M</strong></span>
        </div>
      ))}
    </div>
  );
}

export default function CashflowChart({ cashflows, printMode }: { cashflows: YearCashflow[]; printMode?: boolean }) {
  const data = cashflows.map(cf => ({ year: cf.year, opbrengsten: cf.totalRevenue, kosten: -cf.totalOpex, capex: cf.year === 0 ? cf.netCashflow : 0, cumulatief: cf.cumulativeCashflow }));
  const anim = !printMode;
  return (
    <div className="bg-white rounded-xl border border-[#D9E2EC] shadow-sm p-5 mb-4">
      <div className="text-sm font-bold text-[#0B1F4A] mb-1">📊 20-Jarig Cashflow Overzicht</div>
      <div className="text-[11px] text-[#8899AA] mb-1">Jaarlijkse opbrengsten vs. kosten met cumulatieve cashflow</div>
      <div className="text-[10px] text-[#8899AA] italic mb-4">ℹ️ Toont jaarlijkse inkomsten minus kosten. De lijn is het cumulatieve resultaat.</div>
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.bdr} />
          <XAxis dataKey="year" tick={{ fontSize: 10, fill: C.t3 }} />
          <YAxis yAxisId="bar" tick={{ fontSize: 10, fill: C.t3 }} tickFormatter={v => `€${(v / 1e6).toFixed(1)}M`} />
          <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 10, fill: C.t3 }} tickFormatter={v => `€${(v / 1e6).toFixed(1)}M`} />
          <Tooltip content={<CfTooltip />} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <ReferenceLine yAxisId="bar" y={0} stroke={C.pri} strokeWidth={1} />
          <Bar yAxisId="bar" dataKey="capex" name="CAPEX" fill={C.rd} radius={[3, 3, 0, 0]} barSize={16} stackId="stack" isAnimationActive={anim} />
          <Bar yAxisId="bar" dataKey="opbrengsten" name="Opbrengsten" fill={C.grn} radius={[3, 3, 0, 0]} barSize={16} stackId="stack" isAnimationActive={anim} />
          <Bar yAxisId="bar" dataKey="kosten" name="Kosten" fill="#F87171" radius={[0, 0, 3, 3]} barSize={16} stackId="stack" isAnimationActive={anim} />
          <Line yAxisId="line" dataKey="cumulatief" name="Cumulatief" stroke={C.sec} strokeWidth={2.5} dot={{ r: 2 }} type="monotone" isAnimationActive={anim} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
